import { money } from '@/lib/report-window';
import { PPC_METRIC, type PpcMetricKey } from '@/lib/ppc';

/**
 * How the PPC page names, explains and formats each metric: one table, so a
 * tile, a chart and a table column always say the same thing. Definitions
 * match apps/api api/v3/ads/ppc-analytics.ts.
 */

export interface PpcMetricDef {
  readonly label: string;
  /** The short column header. */
  readonly shortLabel: string;
  /** Shown on hover: what the number is and where it comes from. */
  readonly info: string;
  /** A value as the tile and the table print it. */
  readonly format: (value: number) => string;
  /** A value on a chart axis (shorter for big numbers). */
  readonly axis: (value: number) => string;
  /** A fall is the good direction (costs). */
  readonly downIsGood: boolean;
}

const moneyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compactMoney = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const compactCount = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const count = (value: number): string => Math.round(value).toLocaleString('en-US');
/** Google reports fractional conversions (attribution splits a conversion across clicks). */
const fractional = (value: number): string => value.toLocaleString('en-US', { maximumFractionDigits: 1 });
const percent = (digits: number) => (value: number): string => `${(value * 100).toFixed(digits)}%`;
const times = (value: number): string => `${value.toFixed(1)}×`;

export const PPC_METRIC_DEFS: Readonly<Record<PpcMetricKey, PpcMetricDef>> = {
  [PPC_METRIC.spend]: {
    label: 'Spend',
    shortLabel: 'Spend',
    info: 'What Google Ads charged, from the hourly Ads sync.',
    format: (v) => money.format(v),
    axis: (v) => compactMoney.format(v),
    downIsGood: true,
  },
  [PPC_METRIC.impressions]: {
    label: 'Impressions',
    shortLabel: 'Impr.',
    info: 'Times our ads were shown.',
    format: count,
    axis: (v) => compactCount.format(v),
    downIsGood: false,
  },
  [PPC_METRIC.clicks]: {
    label: 'Clicks',
    shortLabel: 'Clicks',
    info: 'Clicks on our ads.',
    format: count,
    axis: (v) => compactCount.format(v),
    downIsGood: false,
  },
  [PPC_METRIC.ctr]: {
    label: 'Click-through rate',
    shortLabel: 'CTR',
    info: 'Clicks divided by impressions.',
    format: percent(2),
    axis: percent(1),
    downIsGood: false,
  },
  [PPC_METRIC.cpc]: {
    label: 'Avg. cost per click',
    shortLabel: 'CPC',
    info: 'Spend divided by clicks.',
    format: (v) => moneyCents.format(v),
    axis: (v) => moneyCents.format(v),
    downIsGood: true,
  },
  [PPC_METRIC.conversions]: {
    label: 'Conversions',
    shortLabel: 'Conv.',
    info: "Google's conversions: the primary actions Google bids on, as Google counts them (fractional when it splits credit).",
    format: fractional,
    axis: (v) => compactCount.format(v),
    downIsGood: false,
  },
  [PPC_METRIC.costPerConversion]: {
    label: 'Cost per conversion',
    shortLabel: 'Cost / conv.',
    info: "Spend divided by Google's conversions.",
    format: (v) => money.format(v),
    axis: (v) => compactMoney.format(v),
    downIsGood: true,
  },
  [PPC_METRIC.conversionRate]: {
    label: 'Conversion rate',
    shortLabel: 'Conv. rate',
    info: "Google's conversions divided by clicks.",
    format: percent(2),
    axis: percent(1),
    downIsGood: false,
  },
  [PPC_METRIC.leads]: {
    label: 'Counted leads',
    shortLabel: 'Leads',
    info: 'Salesforce leads with a Google click id that count as real leads (the lead classifier, or a person who set the lead to count). By the day the lead arrived. Tied to a campaign and ad group through the click.',
    format: count,
    axis: count,
    downIsGood: false,
  },
  [PPC_METRIC.costPerLead]: {
    label: 'Cost per lead',
    shortLabel: 'Cost / lead',
    info: 'Spend divided by counted leads.',
    format: (v) => money.format(v),
    axis: (v) => compactMoney.format(v),
    downIsGood: true,
  },
  [PPC_METRIC.proposals]: {
    label: 'Proposals',
    shortLabel: 'Proposals',
    info: "Counted leads whose opportunity reached Proposal(s) Sent or a later stage. Counted in the period the lead arrived, so recent periods fill in.",
    format: count,
    axis: count,
    downIsGood: false,
  },
  [PPC_METRIC.costPerProposal]: {
    label: 'Cost per proposal',
    shortLabel: 'Cost / proposal',
    info: 'Spend divided by proposals.',
    format: (v) => money.format(v),
    axis: (v) => compactMoney.format(v),
    downIsGood: true,
  },
  [PPC_METRIC.booked]: {
    label: 'Booked',
    shortLabel: 'Booked',
    info: "Total Cost of the signed opportunities these leads became (never Salesforce Amount). Counted in the period the lead arrived, so recent periods fill in as deals sign.",
    format: (v) => money.format(v),
    axis: (v) => compactMoney.format(v),
    downIsGood: false,
  },
  [PPC_METRIC.roas]: {
    label: 'Return on ad spend',
    shortLabel: 'ROAS',
    info: 'Booked divided by spend: dollars signed per dollar of ads.',
    format: times,
    axis: times,
    downIsGood: false,
  },
  [PPC_METRIC.searchImpressionShare]: {
    label: 'Search impression share',
    shortLabel: 'Search IS',
    info: 'Impressions divided by the impressions we were eligible for, on Search. Performance Max and display report none.',
    format: percent(0),
    axis: percent(0),
    downIsGood: false,
  },
};

/** "—" for a value with nothing to divide by. */
export const NO_VALUE = '—';

export function formatMetric(key: PpcMetricKey, value: number | null): string {
  return value === null ? NO_VALUE : PPC_METRIC_DEFS[key].format(value);
}

/** Percent change from `previous` to `current`, or null when there is nothing to compare. */
export function changePct(current: number | null, previous: number | null | undefined): number | null {
  if (current === null || previous === null || previous === undefined || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

/** The chart picker's groups, so fifteen metrics read as four short lists. */
export const PPC_METRIC_GROUPS: readonly { readonly label: string; readonly metrics: readonly PpcMetricKey[] }[] = [
  { label: 'Cost', metrics: [PPC_METRIC.spend, PPC_METRIC.cpc, PPC_METRIC.costPerConversion, PPC_METRIC.costPerLead, PPC_METRIC.costPerProposal] },
  { label: 'Volume', metrics: [PPC_METRIC.impressions, PPC_METRIC.clicks, PPC_METRIC.conversions, PPC_METRIC.leads, PPC_METRIC.proposals] },
  { label: 'Rates', metrics: [PPC_METRIC.ctr, PPC_METRIC.conversionRate, PPC_METRIC.searchImpressionShare] },
  { label: 'Revenue', metrics: [PPC_METRIC.booked, PPC_METRIC.roas] },
];
